import { useState, useEffect, useCallback, SyntheticEvent } from 'react';

export interface UseImageFallbackOptions {
  retryOnError?: boolean;
  retryDelay?: number;
  maxRetries?: number;
  onLoadSuccess?: () => void;
  onLoadError?: (error: Event) => void;
}

export interface UseImageFallbackReturn {
  imgProps: {
    onError: (e: SyntheticEvent<HTMLImageElement, Event>) => void;
    onLoad: () => void;
  };
  hasError: boolean;
  isLoading: boolean;
  retry: () => void;
  resetError: () => void;
}

export const useImageFallback = (
  src: string,
  options: UseImageFallbackOptions = {}
): UseImageFallbackReturn => {
  const {
    retryOnError = true,
    retryDelay = 1000,
    maxRetries = 2,
    onLoadSuccess,
    onLoadError,
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryTimeout, setRetryTimeout] = useState<NodeJS.Timeout | null>(null);

  // Reset state when src changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setRetryCount(0);
    if (retryTimeout) {
      clearTimeout(retryTimeout);
      setRetryTimeout(null);
    }
  }, [src]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [retryTimeout]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    onLoadSuccess?.();
  }, [onLoadSuccess]);

  const handleError = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      setIsLoading(false);
      setHasError(true);
      onLoadError?.(e.nativeEvent);

      // Retry logic with exponential backoff
      if (retryOnError && retryCount < maxRetries) {
        const timeout = setTimeout(() => {
          setRetryCount((prev) => prev + 1);
          setIsLoading(true);
          setHasError(false);
          // Force image reload by updating src
          const img = e.currentTarget;
          const originalSrc = img.src;
          img.src = '';
          img.src = originalSrc;
        }, retryDelay * Math.pow(2, retryCount));

        setRetryTimeout(timeout);
      }
    },
    [retryOnError, retryCount, maxRetries, retryDelay, onLoadError]
  );

  const retry = useCallback(() => {
    setRetryCount(0);
    setIsLoading(true);
    setHasError(false);
  }, []);

  const resetError = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
  }, []);

  return {
    imgProps: {
      onError: handleError,
      onLoad: handleLoad,
    },
    hasError,
    isLoading,
    retry,
    resetError,
  };
};